import { useEffect, useState } from "react";

import { RolesApi } from "@/Administration/Api/Roles/RolesApi";
import { RoleModel } from "@/Administration/Models/Role/RoleModel";
import { UsersListFieldComponent } from "@/Common/Components/AddEmployeePopup/UsersListFieldComponent";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { FormMode } from "@/Common/Models/UI/Enums";
import { RemoveAllUsersWarningPopup } from "./RemoveAllUsersWarningPopup/RemoveAllUsersWarningPopup";

import style from "./RoleDetailsEditComponent.module.scss";

interface IRoleDetailsEditComponentProps {
    onChange: (role: RoleModel) => void;
    role: RoleModel;
    disableEditing: boolean;
}

class RemoveAllUsersState {
    public isVisible: boolean = false;
    public roleExclusiveUsers: Person[] = [];
    public startRemoveUsersAction: () => void = null;
}

function RoleDetailsEditComponent(props: Readonly<IRoleDetailsEditComponentProps>): JSX.Element {
    const { getText } = useTranslation();

    const [removeUsersWarningState, setRemoveUsersWarningState] =
        useState(new RemoveAllUsersState());

    const filteredEmployees = cacheStore.GetClinicians(false)
        .filter(person => !props.role.UserIds?.any(userId => userId === person.EmployeeModel.User_id));

    useEffect(
        () => {
            if (!props.disableEditing)
                document.getElementById("roleNameInput")?.focus();
        },
        [props.role.Id, props.disableEditing]
    );

    const handleEmployeesListChange = (value: Person[]): void => {
        props.onChange({ ...props.role, UserIds: value.map(p => p.EmployeeModel.User_id) });
    };

    const removeUsersWithWarning = async (usersToRemove: Person[], removeAction: () => void): Promise<void> => {
        if (!usersToRemove.any())
            return;

        const roleExclusiveUsers = (await RolesApi
            .GetRoleExclusiveUsers(props.role.Id))
            .UserIds
            .filter(userId => usersToRemove
                .map(user => user.EmployeeModel.User_id)
                .includes(userId)
            );

        if (roleExclusiveUsers.any())
            return showRemoveUsersWarningPopup(roleExclusiveUsers, removeAction);
        else
            removeAction();
    };

    const showRemoveUsersWarningPopup = (roleExclusiveUsers: string[], removeUsersAction: () => void): void => {
        setRemoveUsersWarningState({
            isVisible: true,
            roleExclusiveUsers: [...getEmployees(roleExclusiveUsers)],
            startRemoveUsersAction: removeUsersAction
        });
    };

    const getEmployees = (userIds: string[]): Person[] => {
        if (!userIds) return [];

        const cliniciansMap = new Map(
            cacheStore.GetClinicians(true).map(clinician => [clinician.EmployeeModel.User_id, clinician])
        );

        return userIds.map(id => cliniciansMap.get(id));
    };

    return (
        <div className={`${style.component} ${props.disableEditing ? style.viewMode : ""}`}>
            <h3>{getText("GeneralInformationLabel")}</h3>

            <label>{getText("RoleNameLabel")}</label>

            <TextboxControl
                dataCy="roleNameInput"
                maxLength={50}
                id="roleNameInput"
                className="form-control"
                value={props.role?.Name ?? ""}
                onChange={value => props.onChange({ ...props.role, Name: value })}
                disabled={props.disableEditing}
            />

            <label className={style.labelForMultilineField}>
                {getText("DescriptionLabel")}
            </label>

            <textarea
                id="roleDescriptionInput"
                onChange={element => props.onChange({ ...props.role, Description: element.target.value })}
                data-cy="roleDescriptionInput"
                disabled={props.disableEditing}
                value={props.role?.Description}
                maxLength={250}
                className="form-control"
            />

            <label className={style.labelForMultilineField}>{getText("Allowlist_ListOfUsersLabel")}</label>

            <UsersListFieldComponent
                selectedEmployeesList={getEmployees(props.role?.UserIds ?? [])}
                setSelectedEmployees={handleEmployeesListChange}
                searchableEmployees={filteredEmployees
                    .map(person => new SearchableItemModelT<Person>(person.NameWithIdentifier, person.ContactFacetId, person))
                    .toImmutable()
                }
                dataCy="role-detail-users-list"
                mode={props.disableEditing ? FormMode.View : FormMode.Edit}
                className={style.usersList}
                onRemoveUsers={removeUsersWithWarning}
            />

            {removeUsersWarningState.isVisible && (
                <RemoveAllUsersWarningPopup
                    employees={removeUsersWarningState.roleExclusiveUsers}
                    onClose={() => setRemoveUsersWarningState(new RemoveAllUsersState())}
                    onContinue={() => {
                        removeUsersWarningState.startRemoveUsersAction();
                        setRemoveUsersWarningState(new RemoveAllUsersState());
                    }}
                />
            )}
        </div>
    );
}

export { RoleDetailsEditComponent };
